import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { send } from 'process';
import { StepValidationState } from 'projects/reg-hub-client/src/interfaces/step';
import { SteppedComponent } from 'projects/reg-hub-client/src/interfaces/stepped-component';
import { OrderManagerService, ValidationService, ConfigurationState, eOrderType, ValidationProblem, Order } from 'reg-hub-common';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-know-your-customer-criteria',
  templateUrl: './know-your-customer-criteria.component.html',
  styleUrl: './know-your-customer-criteria.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class KnowYourCustomerCriteriaComponent extends SteppedComponent {
  protected criteria!: FormGroup;

  protected shouldDoCreditPhoneWaterfallValidationErrors$ = new BehaviorSubject<string | null | undefined>(null);
  protected shouldDoCreditBureauValidationErrors$ = new BehaviorSubject<string | null | undefined>(null);
  protected shouldDoIdentityVerificationErrors$ = new BehaviorSubject<string | null | undefined>(null);
  protected shouldDoPhoneNumberValidationErrors$ = new BehaviorSubject<string | null | undefined>(null);
  protected shouldDoPepsAndSanctionsErrors$ = new BehaviorSubject<string | null | undefined>(null);

  protected shouldShowPepsAndSanctions: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private configurationState: ConfigurationState,
    orderManager: OrderManagerService,
    validationService: ValidationService) { super(orderManager, validationService); }

  override ngOnInit(): void {
    super.ngOnInit();

    this.configurationState.canSubmitOrderType(eOrderType.pepsAndSanctions).then(val => this.shouldShowPepsAndSanctions = val);

    if (!this.order.knowYourCustomerCriteria) {
      this.order.knowYourCustomerCriteria = {
        shouldDoCreditPhoneWaterfallValidation: false,
        shouldDoCreditBureauValidation: false,
        shouldDoPhoneNumberValidation: false,
        shouldDoIdentityVerification: false,
        shouldDoPepsAndSanctions: false,
        sendIdvInFrench: false
      };
    }

    this.criteria = this.formBuilder.group({
      shouldDoCreditPhoneWaterfallValidation: [this.order.knowYourCustomerCriteria?.shouldDoCreditPhoneWaterfallValidation ?? false],
      shouldDoCreditBureauValidation: [this.order.knowYourCustomerCriteria?.shouldDoCreditBureauValidation ?? false],
      shouldDoIdentityVerification: [this.order.knowYourCustomerCriteria?.shouldDoIdentityVerification ?? false],
      shouldDoPhoneNumberValidation: [this.order.knowYourCustomerCriteria?.shouldDoPhoneNumberValidation ?? false],
      shouldDoPepsAndSanctions: [this.order.knowYourCustomerCriteria?.shouldDoPepsAndSanctions ?? false],
      sendIdvInFrench: [this.order.knowYourCustomerCriteria?.sendIdvInFrench ?? false]
    });

    this.applyDefaults();

    this.setUIConfiguration();

    this.setCreditPhoneCheckboxesDisabled();
  }

  private applyDefaults(): void {
    const hasExistingSelections =
      this.order.knowYourCustomerCriteria?.shouldDoCreditPhoneWaterfallValidation ||
      this.order.knowYourCustomerCriteria?.shouldDoCreditBureauValidation ||
      this.order.knowYourCustomerCriteria?.shouldDoPhoneNumberValidation ||
      this.order.knowYourCustomerCriteria?.shouldDoIdentityVerification ||
      this.order.knowYourCustomerCriteria?.shouldDoPepsAndSanctions;

    // don't set defaults if there are existing selections to avoid overwriting them, but if there are no selections apply the corporation defaults
    if (hasExistingSelections) {
      return;
    }

    this.configurationState.getKnowYourCustomerDefaults().then(defaults => {
      if (!defaults) {
        return;
      }

      this.criteria.patchValue({
        shouldDoCreditPhoneWaterfallValidation: defaults.shouldDoCreditPhoneWaterfallValidation,
        shouldDoCreditBureauValidation: defaults.shouldDoCreditBureauValidation,
        shouldDoIdentityVerification: defaults.shouldDoIdentityVerification,
        shouldDoPhoneNumberValidation: defaults.shouldDoPhoneNumberValidation,
        // just in case someone leaves it defaulted but hides it at the same time
        shouldDoPepsAndSanctions: this.shouldShowPepsAndSanctions && defaults.shouldDoPepsAndSanctions,
        sendIdvInFrench: defaults.sendIdvInFrench
      });

      this.setUIConfiguration();

      this.setCreditPhoneCheckboxesDisabled();
    });
  }

  public override onSaving(): void {
    const rawValue = this.criteria.getRawValue();
    const party = this.order.knowYourCustomerCriteria?.party;
    this.order.knowYourCustomerCriteria = rawValue;
    this.order.knowYourCustomerCriteria!.party = party;
  }

  public override getValidationState(errors: ValidationProblem[] | undefined, order: Order): StepValidationState {
    return order.knowYourCustomerCriteria?.shouldDoCreditPhoneWaterfallValidation ||
      order.knowYourCustomerCriteria?.shouldDoCreditBureauValidation ||
      order.knowYourCustomerCriteria?.shouldDoIdentityVerification ||
      order.knowYourCustomerCriteria?.shouldDoPhoneNumberValidation ||
      order.knowYourCustomerCriteria?.shouldDoPepsAndSanctions ? StepValidationState.ValidationSuccess : StepValidationState.ValidationError;
  }

  protected setUIConfiguration() {
    this.setPhoneNumberVisibility();
    this.setEmailVisibility();
    this.setAddressVisibility();
  }

  protected setPhoneNumberVisibility() {
    if (!this.uiConfiguration.debtorsConfiguration?.contactDetailsConfiguration) {
      return;
    }
    const showPhone = this.criteria.value.shouldDoPhoneNumberValidation || this.criteria.value.shouldDoCreditPhoneWaterfallValidation;

    this.uiConfiguration!.debtorsConfiguration!.contactDetailsConfiguration!.showPhoneNumber = showPhone;
  }

  protected setEmailVisibility() {
    if (!this.uiConfiguration.debtorsConfiguration?.contactDetailsConfiguration) {
      return;
    }
    const showEmail = this.criteria.value.shouldDoIdentityVerification;

    this.uiConfiguration!.debtorsConfiguration!.contactDetailsConfiguration!.showEmail = showEmail;
  }

  protected setAddressVisibility() {
    if (!this.uiConfiguration.debtorsConfiguration?.contactDetailsConfiguration) {
      return;
    }
    const showAddress = (this.criteria.value.shouldDoCreditBureauValidation || this.criteria.value.shouldDoPhoneNumberValidation || this.criteria.value.shouldDoCreditPhoneWaterfallValidation);

    this.uiConfiguration!.debtorsConfiguration!.contactDetailsConfiguration!.showIndividualAddress = showAddress;
  }

  protected toggleCreditPhoneWaterfall(isChecked: boolean) {
    if (isChecked) {
      this.criteria.setValue({
        ...this.criteria.value,
        shouldDoCreditBureauValidation: false,
        shouldDoPhoneNumberValidation: false
      });
      this.criteria.get('shouldDoCreditBureauValidation')?.disable();
      this.criteria.get('shouldDoPhoneNumberValidation')?.disable();

      this.setUIConfiguration();
    } else {
      this.criteria.get('shouldDoPhoneNumberValidation')?.enable();
      this.criteria.get('shouldDoCreditBureauValidation')?.enable();
    }
  }

  private setCreditPhoneCheckboxesDisabled() {
    if (this.criteria.get('shouldDoCreditPhoneWaterfallValidation')?.value) {
      this.criteria.get('shouldDoCreditBureauValidation')?.disable();
      this.criteria.get('shouldDoPhoneNumberValidation')?.disable();
    } else {
      this.criteria.get('shouldDoCreditBureauValidation')?.enable();
      this.criteria.get('shouldDoPhoneNumberValidation')?.enable();
    }
  }
}
